import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/layout/Header';
import AddToRequirementDialog from '../components/requirements/AddToRequirementDialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { 
  FileText, ArrowLeft, Upload, Loader2, Pencil, Trash2, 
  User, Mail, Phone, Building2, MapPin, Calendar, Briefcase,
  IndianRupee, Clock, CheckCircle, XCircle, UserPlus, Download,
  ChevronRight, ChevronDown, Users, Target, Plus, Share2, Link2, Copy, ExternalLink,
  Video, RefreshCw, Eye, History, ShieldCheck, Send
} from 'lucide-react';
import { toast } from 'sonner';
import ScheduleInterviewDialog from '../components/interviews/ScheduleInterviewDialog'; // interviews-v2 (2026-09-07)
import WhoFitsPanel from '../components/matchview/WhoFitsPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_CONFIG = {
  uploaded: { label: 'Uploaded', color: 'bg-slate-100 text-slate-700', order: 0 },
  screened: { label: 'Screened', color: 'bg-blue-100 text-blue-700', order: 1 },
  shortlisted: { label: 'Shortlisted', color: 'bg-cyan-100 text-cyan-700', order: 2 },
  lined_up: { label: 'Lined Up', color: 'bg-indigo-100 text-indigo-700', order: 3 },
  interviewed: { label: 'Interviewed', color: 'bg-purple-100 text-purple-700', order: 4 },
  offered: { label: 'Offered', color: 'bg-amber-100 text-amber-700', order: 5 },
  joined: { label: 'Joined', color: 'bg-green-100 text-green-700', order: 6 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', order: 7 }
};

const STAGES = Object.keys(STAGE_CONFIG);

export default function RequirementDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  const [requirement, setRequirement] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [stageCounts, setStageCounts] = useState({});
  const [loading, setLoading] = useState(true);
  // Honour ?stage=<key> deep links (Dashboard pipeline table / My Desk board) - was always 'all'.
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const st = searchParams.get('stage');
    return st && STAGE_CONFIG[st] ? st : 'all';
  });
  
  // Upload Resume Modal
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [aiProvider, setAiProvider] = useState('anthropic');
  const [uploading, setUploading] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [resumePath, setResumePath] = useState('');
  const [resumeFilename, setResumeFilename] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Add candidates from the database (many at once) - routes/requirement_links.py
  const [addCandidatesOpen, setAddCandidatesOpen] = useState(false);

  // Manual Add Modal
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [manualForm, setManualForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    current_company: '',
    current_designation: '',
    total_experience_years: '',
    current_ctc: '',
    expected_ctc: '',
    notice_period_days: '',
    location: '',
    skills: '',
    summary: ''
  });
  
  // Matching Candidates
  const [matchingCandidates, setMatchingCandidates] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [addingFromDb, setAddingFromDb] = useState(null);
  
  // Delete
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Dossier - Build 3 Enhanced
  const [generatingDossier, setGeneratingDossier] = useState(null);
  const [generatingPdfDossier, setGeneratingPdfDossier] = useState(null);
  const [dossierModalOpen, setDossierModalOpen] = useState(false);
  const [dossierLink, setDossierLink] = useState(null);
  const [dossierResultModalOpen, setDossierResultModalOpen] = useState(false);
  const [generatedDossierResult, setGeneratedDossierResult] = useState(null);
  const [candidateDossiers, setCandidateDossiers] = useState({});
  const [loadingDossiers, setLoadingDossiers] = useState({});
  const [dossierSectionsOpen, setDossierSectionsOpen] = useState({});
  const [regeneratingDossier, setRegeneratingDossier] = useState(null);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [adminTemplateOverride, setAdminTemplateOverride] = useState('auto');
  
  // Redacted CV Generator
  const [redactedCvModalOpen, setRedactedCvModalOpen] = useState(false);
  const [selectedCandidateForRedactedCv, setSelectedCandidateForRedactedCv] = useState(null);
  const [generatingRedactedCv, setGeneratingRedactedCv] = useState(false);
  const [redactedCvOptions, setRedactedCvOptions] = useState({ hideName: false, hideEmployers: false });
  const [redactedCvResultModalOpen, setRedactedCvResultModalOpen] = useState(false);
  const [generatedRedactedCvResult, setGeneratedRedactedCvResult] = useState(null);
  
  // Interview Scheduling
  const [interviewModalOpen, setInterviewModalOpen] = useState(false);
  const [selectedCandidateForInterview, setSelectedCandidateForInterview] = useState(null);
  const [schedulingInterview, setSchedulingInterview] = useState(false);
  const [interviewForm, setInterviewForm] = useState({
    round_name: 'Interview',
    mode: 'phone',
    scheduled_at: '',
    location: '',
    meeting_link: '',
    client_contact_name: '',
    client_contact_phone: '',
    notes: ''
  });

  // Multi-select for Client Submission
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);

  // Joined = a placement (Phase 5, Record the money). Moving a candidate to Joined opens a
  // box for the signed CTC / fee override / joining date; the same stage endpoint is called
  // with the signed_ctc / fee_percentage_override / joining_date form fields it already accepts.
  const [joinedModalOpen, setJoinedModalOpen] = useState(false);
  const [joinedCandidate, setJoinedCandidate] = useState(null);
  const [joinedForm, setJoinedForm] = useState({ signed_ctc: '', fee_percentage_override: '', joining_date: '' });
  const [savingJoined, setSavingJoined] = useState(false);

  // Toggle candidate selection for submission
  const toggleCandidateSelection = (candidateId) => {
    setSelectedCandidateIds(prev => {
      if (prev.includes(candidateId)) {
        return prev.filter(id => id !== candidateId);
      } else {
        return [...prev, candidateId];
      }
    });
  };

  // Select/Deselect all candidates
  const toggleSelectAll = () => {
    if (selectedCandidateIds.length === candidates.length) {
      setSelectedCandidateIds([]);
    } else {
      setSelectedCandidateIds(candidates.map(c => c.id));
    }
  };

  // Navigate to Communications page with pre-selected data
  const openSubmissionComposer = () => {
    if (selectedCandidateIds.length === 0) {
      toast.error('Please select at least one candidate');
      return;
    }
    
    // Navigate to Communications page with query params
    const params = new URLSearchParams({
      client_id: requirement?.client_id || '',
      requirement_id: id,
      candidate_ids: selectedCandidateIds.join(','),
      auto_open: 'true'
    });
    
    navigate(`/ats/manual-selections/communications?${params.toString()}`);
  };

  const loadCandidates = useCallback(async () => {
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/requirements/${id}/candidates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCandidates(data.candidates || []);
        setStageCounts(data.stage_counts || {});
        setRequirement(data.requirement || null);
      } else {
        toast.error('Failed to load candidates');
      }
    } catch (e) {
      console.error('Error loading candidates:', e);
      toast.error('Error loading candidates');
    }
    setLoading(false);
  }, [id]);

  const loadMatchingCandidates = useCallback(async () => {
    setLoadingMatches(true);
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/requirements/${id}/matching-candidates?min_match=90`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMatchingCandidates(data.candidates || []);
      }
    } catch (e) {
      console.error('Error loading matching candidates:', e);
    }
    setLoadingMatches(false);
  }, [id]);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  // ?dossier_for=<rc_id>[,<rc_id>] - "Create a dossier now" from AddToRequirementDialog lands here:
  // open those rows' dossier sections and scroll the first one into view. Runs once per page load.
  const [dossierForHandled, setDossierForHandled] = useState(false);
  useEffect(() => {
    if (dossierForHandled || loading || candidates.length === 0) return;
    let wanted = [];
    try { wanted = (new URLSearchParams(window.location.search).get('dossier_for') || '').split(',').filter(Boolean); } catch (e) { wanted = []; }
    if (wanted.length === 0) return;
    setDossierForHandled(true);
    const present = wanted.filter((rcId) => candidates.some((c) => c.id === rcId));
    if (present.length === 0) { toast.error('Candidate not found on this requirement'); return; }
    setActiveTab('all');
    present.forEach((rcId) => { if (!dossierSectionsOpen[rcId]) toggleDossierSection(rcId); });
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="candidate-card-${present[0]}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-emerald-500');
        setTimeout(() => el.classList.remove('ring-2', 'ring-emerald-500'), 4000);
      }
    }, 250);
    toast.success(present.length === 1 ? 'Ready - generate the dossier from this candidate\'s card' : `Ready - ${present.length} candidates highlighted`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, candidates, dossierForHandled]);

  useEffect(() => {
    if (requirement) {
      loadMatchingCandidates();
    }
  }, [requirement, loadMatchingCandidates]);

  // Load templates for admin override (Build 3)
  const loadTemplates = useCallback(async () => {
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/dossier-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAvailableTemplates(data.templates || []);
      }
    } catch (e) {
      console.error('Error loading templates:', e);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadTemplates();
    }
  }, [isAdmin, loadTemplates]);

  // Load saved dossiers for a candidate (Build 3)
  const loadCandidateDossiers = useCallback(async (candidateId) => {
    setLoadingDossiers(prev => ({ ...prev, [candidateId]: true }));
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/requirements/${id}/candidates/${candidateId}/dossiers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCandidateDossiers(prev => ({ ...prev, [candidateId]: data.dossiers || [] }));
      }
    } catch (e) {
      console.error('Error loading dossiers:', e);
    }
    setLoadingDossiers(prev => ({ ...prev, [candidateId]: false }));
  }, [id]);

  // Toggle dossier section for a candidate
  const toggleDossierSection = (candidateId) => {
    const isOpen = dossierSectionsOpen[candidateId];
    setDossierSectionsOpen(prev => ({ ...prev, [candidateId]: !isOpen }));
    if (!isOpen && !candidateDossiers[candidateId]) {
      loadCandidateDossiers(candidateId);
    }
  };

  const resetUploadModal = () => {
    setSelectedFile(null);
    setParsedData(null);
    setResumePath('');
    setResumeFilename('');
  };

  const handleResumeUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a resume file');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('ats_token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('ai_provider', aiProvider);

      const res = await fetch(`${API_URL}/api/requirements/${id}/candidates/upload-resume`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setParsedData(data.parsed_data);
        setResumePath(data.resume_storage_path);
        setResumeFilename(data.resume_filename);
        if (data.parsed_data?.error) {
          toast.error(`AI parsing failed: ${data.parsed_data.error}`);
        } else {
          toast.success('Resume parsed! Review and save.');
        }
      } else {
        let msg = 'Failed to parse resume';
        try {
          const err = await res.json();
          msg = typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail);
        } catch {
          msg = `Server error (${res.status}). Please try again.`;
        }
        toast.error(msg);
      }
    } catch (e) {
      console.error('Upload error:', e);
      toast.error(`Error uploading resume: ${e.message}`);
    }
    setUploading(false);
  };

  const handleSaveFromResume = async () => {
    if (!parsedData?.full_name) {
      toast.error('Candidate name is required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('ats_token');
      const formData = new FormData();
      formData.append('full_name', parsedData.full_name || '');
      formData.append('email', parsedData.email || '');
      formData.append('phone', parsedData.phone || '');
      formData.append('current_company', parsedData.current_company || '');
      formData.append('current_designation', parsedData.current_designation || '');
      formData.append('total_experience_years', parsedData.total_experience_years || '');
      formData.append('current_ctc', parsedData.current_ctc || '');
      formData.append('expected_ctc', parsedData.expected_ctc || '');
      formData.append('notice_period_days', parsedData.notice_period_days || '');
      formData.append('location', parsedData.location || '');
      formData.append('skills', JSON.stringify(parsedData.skills || []));
      formData.append('summary', parsedData.summary || '');
      formData.append('resume_storage_path', resumePath);
      formData.append('resume_filename', resumeFilename);
      formData.append('resume_parsed_json', JSON.stringify(parsedData));

      const res = await fetch(`${API_URL}/api/requirements/${id}/candidates/create-from-resume`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success === false) {
          toast.error(data.detail || 'Could not add candidate');
        } else {
          toast.success(data.message || 'Candidate added successfully');
          setUploadModalOpen(false);
          resetUploadModal();
          loadCandidates();
          loadMatchingCandidates();
        }
      } else {
        let msg = 'Failed to add candidate';
        try {
          const err = await res.json();
          msg = typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail);
        } catch {
          msg = `Server error (${res.status}). Please try again.`;
        }
        toast.error(msg);
      }
    } catch (e) {
      console.error('Save error:', e);
      toast.error(`Error saving candidate: ${e.message}`);
    }
    setSaving(false);
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualForm.full_name.trim()) {
      toast.error('Candidate name is required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('ats_token');
      const payload = {
        requirement_id: id,
        full_name: manualForm.full_name,
        email: manualForm.email || null,
        phone: manualForm.phone || null,
        current_company: manualForm.current_company || null,
        current_designation: manualForm.current_designation || null,
        total_experience_years: manualForm.total_experience_years ? parseFloat(manualForm.total_experience_years) : null,
        current_ctc: manualForm.current_ctc ? parseFloat(manualForm.current_ctc) : null,
        expected_ctc: manualForm.expected_ctc ? parseFloat(manualForm.expected_ctc) : null,
        notice_period_days: manualForm.notice_period_days ? parseInt(manualForm.notice_period_days) : null,
        location: manualForm.location || null,
        skills: manualForm.skills ? manualForm.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
        summary: manualForm.summary || null
      };

      const res = await fetch(`${API_URL}/api/requirements/${id}/candidates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success('Candidate added');
        setManualModalOpen(false);
        setManualForm({
          full_name: '', email: '', phone: '', current_company: '', current_designation: '',
          total_experience_years: '', current_ctc: '', expected_ctc: '', notice_period_days: '',
          location: '', skills: '', summary: ''
        });
        loadCandidates();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add candidate');
      }
    } catch (e) {
      console.error('Error:', e);
      toast.error('Error adding candidate');
    }
    setSaving(false);
  };

  // The pipeline row stores CTC the way it was typed - usually lakhs ("12" = 12 LPA). The
  // placement needs rupees, so a figure under 1000 is treated as lakhs when prefilling.
  const ctcInRupees = (v) => {
    const n = parseFloat(String(v ?? '').replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(n) || n <= 0) return '';
    return String(Math.round(n < 1000 ? n * 100000 : n));
  };

  const openJoinedModal = (cand) => {
    setJoinedCandidate(cand);
    setJoinedForm({
      signed_ctc: ctcInRupees(cand?.expected_ctc || cand?.current_ctc),
      fee_percentage_override: '',
      joining_date: new Date().toISOString().slice(0, 10)
    });
    setJoinedModalOpen(true);
  };

  const submitJoined = async (withCtc) => {
    if (!joinedCandidate) return;
    const ctc = parseFloat(joinedForm.signed_ctc);
    if (withCtc && !(ctc > 0)) {
      toast.error('Enter the signed annual CTC in rupees (e.g. 1200000 for 12 LPA), or Skip');
      return;
    }
    setSavingJoined(true);
    try {
      await handleStageChange(joinedCandidate.id, 'joined', {
        signed_ctc: withCtc ? ctc : null,
        fee_percentage_override: withCtc ? parseFloat(joinedForm.fee_percentage_override) || null : null,
        joining_date: joinedForm.joining_date || null
      });
      setJoinedModalOpen(false);
    } finally {
      setSavingJoined(false);
    }
  };

  const handleStageChange = async (candidateId, newStage, placement = null) => {
    if (newStage === 'joined' && !placement) {
      openJoinedModal(candidates.find(c => c.id === candidateId) || { id: candidateId });
      return;
    }
    try {
      const token = localStorage.getItem('ats_token');
      const formData = new FormData();
      formData.append('status', newStage);
      if (placement) {
        if (placement.signed_ctc) formData.append('signed_ctc', String(placement.signed_ctc));
        if (placement.fee_percentage_override) formData.append('fee_percentage_override', String(placement.fee_percentage_override));
        if (placement.joining_date) formData.append('joining_date', placement.joining_date);
      }

      const res = await fetch(`${API_URL}/api/requirements/${id}/candidates/${candidateId}/stage`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        toast.success(
          newStage === 'joined'
            ? (placement && placement.signed_ctc
                ? `Joined - placement recorded at Rs ${Number(placement.signed_ctc).toLocaleString('en-IN')} signed CTC`
                : 'Joined - placement recorded provisionally from the CTC on file (confirm it in Hired Candidates)')
            : `Stage updated to ${STAGE_CONFIG[newStage].label}`
        );
        loadCandidates();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update stage');
      }
    } catch (e) {
      console.error('Error:', e);
      toast.error('Error updating stage');
    }
  };

  const handleAddFromDatabase = async (sourceCandidateId) => {
    setAddingFromDb(sourceCandidateId);
    try {
      const token = localStorage.getItem('ats_token');
      const formData = new FormData();
      formData.append('source_candidate_id', sourceCandidateId);

      const res = await fetch(`${API_URL}/api/requirements/${id}/candidates/add-from-database`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        toast.success('Candidate added from database');
        loadCandidates();
        loadMatchingCandidates();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add candidate');
      }
    } catch (e) {
      console.error('Error:', e);
      toast.error('Error adding candidate');
    }
    setAddingFromDb(null);
  };

  const confirmDelete = (candidate) => {
    setCandidateToDelete(candidate);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!candidateToDelete) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/requirements/${id}/candidates/${candidateToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Candidate removed');
        setDeleteConfirmOpen(false);
        setCandidateToDelete(null);
        loadCandidates();
        loadMatchingCandidates();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete');
      }
    } catch (e) {
      console.error('Error:', e);
      toast.error('Error deleting candidate');
    }
    setDeleting(false);
  };

  const handleGenerateDossier = async (candidateId) => {
    setGeneratingDossier(candidateId);
    try {
      const token = localStorage.getItem('ats_token');
      const formData = new FormData();
      formData.append('expires_days', '30');
      formData.append('ai_provider', 'anthropic');

      const res = await fetch(`${API_URL}/api/requirements/${id}/candidates/${candidateId}/generate-dossier`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setDossierLink(data);
        setDossierModalOpen(true);
        toast.success('Dossier generated successfully!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to generate dossier');
      }
    } catch (e) {
      console.error('Error:', e);
      toast.error('Error generating dossier');
    }
    setGeneratingDossier(null);
  };

  const copyDossierLink = () => {
    const fullUrl = `${window.location.origin}/cv/${dossierLink?.token}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('Link copied to clipboard!');
  };

  // Copy share link for a saved dossier
  const copyShareLink = (shareToken) => {
    const fullUrl = `${window.location.origin}/cv/${shareToken}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('Share link copied to clipboard!');
  };

  // Generate PDF Dossier using Handlebars + Playwright pipeline (Build 3 - Enhanced)
  const handleGeneratePdfDossier = async (candidateId) => {
    setGeneratingPdfDossier(candidateId);
    const candidate = candidates.find(c => c.id === candidateId);
    const token = localStorage.getItem('ats_token');

    try {
      const formData = new FormData();
      // Admin can override template, recruiters get AI auto-selection
      if (isAdmin && adminTemplateOverride !== 'auto') {
        formData.append('template_id', adminTemplateOverride);
      }

      const res = await fetch(`${API_URL}/api/requirements/${id}/candidates/${candidateId}/generate-pdf-dossier`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        const dossierRef = data.dossier_ref;
        toast.info('Working on it\u2026 this can take up to a couple of minutes.');

        pollRedactedCvStatus(dossierRef, {
          onReady: async () => {
            try {
              const [pdfRes, infoRes] = await Promise.all([
                fetch(`${API_URL}/api/dossiers/${dossierRef}/pdf`, {
                  headers: { Authorization: `Bearer ${token}` }
                }),
                fetch(`${API_URL}/api/dossiers/${dossierRef}`, {
                  headers: { Authorization: `Bearer ${token}` }
                })
              ]);
              const blob = await pdfRes.blob();
              const info = infoRes.ok ? await infoRes.json() : {};
              const candidateName = candidate?.full_name?.replace(/\s+/g, '_') || 'Candidate';
              const position = requirement?.position_title?.replace(/\s+/g, '_') || 'Position';
              const filename = `Dossier_${candidateName}_${position}.pdf`;
              const pdfUrl = window.URL.createObjectURL(blob);

              setGeneratedDossierResult({
                candidateId,
                candidateName: candidate?.full_name || 'Candidate',
                dossierRef,
                shareToken: info.share_token,
                templateUsed: info.template_id,
                pdfUrl,
                filename
              });
              setDossierResultModalOpen(true);
              loadCandidateDossiers(candidateId);
              toast.success('PDF Dossier generated successfully!');
            } catch (e) {
              console.error('Error finishing PDF dossier:', e);
              toast.error('Error generating PDF dossier');
            }
            setGeneratingPdfDossier(null);
          },
          onFailed: () => {
            toast.error('Failed to generate PDF dossier');
            setGeneratingPdfDossier(null);
          },
          onTimeout: () => {
            toast.error('Still working on it \u2014 check back in the Documents list shortly.');
            setGeneratingPdfDossier(null);
          }
        });
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to generate PDF dossier');
        setGeneratingPdfDossier(null);
      }
    } catch (e) {
      console.error('Error generating PDF:', e);
      toast.error('Error generating PDF dossier');
      setGeneratingPdfDossier(null);
    }
  };


  // Download dossier PDF
  const downloadDossierPdf = () => {
    if (generatedDossierResult?.pdfUrl) {
      const link = document.createElement('a');
      link.href = generatedDossierResult.pdfUrl;
      link.download = generatedDossierResult.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('PDF downloaded!');
    }
  };

  // Download saved dossier by ref
  const downloadSavedDossierPdf = async (dossierRef) => {
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/dossiers/${dossierRef}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Dossier_${dossierRef}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('PDF downloaded!');
      } else {
        toast.error('Failed to download PDF');
      }
    } catch (e) {
      toast.error('Error downloading PDF');
    }
  };

  // Regenerate dossier (available to all users)
  const handleRegenerateDossier = async (dossierRef, candidateId) => {
    setRegeneratingDossier(dossierRef);
    try {
      const token = localStorage.getItem('ats_token');
      const formData = new FormData();
      // Only admin can override template during regeneration
      if (isAdmin && adminTemplateOverride !== 'auto') {
        formData.append('template_id', adminTemplateOverride);
      }

      const res = await fetch(`${API_URL}/api/dossiers/${dossierRef}/regenerate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Dossier_${dossierRef}_regenerated.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        // Refresh dossiers list
        loadCandidateDossiers(candidateId);
        toast.success('Dossier regenerated and downloaded!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to regenerate dossier');
      }
    } catch (e) {
      toast.error('Error regenerating dossier');
    }
    setRegeneratingDossier(null);
  };

  // View dossier HTML preview
  const viewDossierPreview = (shareToken) => {
    window.open(`${API_URL}/api/public/dossier/${shareToken}`, '_blank');
  };

  // =========================================================================
  // REDACTED CV GENERATOR
  // =========================================================================
  
  // Open redacted CV modal with options
  const openRedactedCvModal = (candidate) => {
    setSelectedCandidateForRedactedCv(candidate);
    setRedactedCvOptions({ hideName: false, hideEmployers: false });
    setRedactedCvModalOpen(true);
  };

  // 2026-09-13: both /generate-redacted-cv and /regenerate-redacted-cv now
  // kick off the slow LLM + PDF work in the background and return right
  // away with {status:"processing", dossier_ref}, instead of blocking the
  // request for 30-90s and streaming the finished PDF back (that used to
  // make the site's own gateway hang up before the response arrived, even
  // though the server kept working). This polls the existing
  // GET /api/dossiers/{dossier_ref} status endpoint every few seconds -
  // same "keep checking until parsing_status flips" pattern already used
  // for candidate CV parsing on the onboarding page - until the record's
  // generation_status becomes "ready" or "failed", or ~2 minutes pass.
  const pollRedactedCvStatus = useCallback((dossierRef, { onReady, onFailed, onTimeout }) => {
    const token = localStorage.getItem('ats_token');
    const POLL_INTERVAL_MS = 4000;
    const MAX_ATTEMPTS = 30; // ~2 minutes

    let attempts = 0;
    let pollInterval;

    const check = async () => {
      attempts += 1;
      try {
        const res = await fetch(`${API_URL}/api/dossiers/${dossierRef}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.generation_status === 'ready') {
            clearInterval(pollInterval);
            onReady(data);
            return;
          }
          if (data.generation_status === 'failed') {
            clearInterval(pollInterval);
            onFailed(data.error);
            return;
          }
        }
      } catch (e) {
        console.error('Redacted CV status poll failed:', e);
      }
      if (attempts >= MAX_ATTEMPTS) {
        clearInterval(pollInterval);
        onTimeout();
      }
    };

    pollInterval = setInterval(check, POLL_INTERVAL_MS);
    check();
  }, []);

  // Generate Redacted CV
  const handleGenerateRedactedCv = async () => {
    if (!selectedCandidateForRedactedCv) return;

    setGeneratingRedactedCv(true);
    const candidate = selectedCandidateForRedactedCv;
    const options = { ...redactedCvOptions };
    const token = localStorage.getItem('ats_token');

    try {
      const formData = new FormData();
      formData.append('hide_name', options.hideName);
      formData.append('hide_employers', options.hideEmployers);

      const res = await fetch(`${API_URL}/api/requirements/${id}/candidates/${candidate.id}/generate-redacted-cv`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        const dossierRef = data.dossier_ref;
        toast.info('Working on it… this can take up to a couple of minutes.');

        pollRedactedCvStatus(dossierRef, {
          onReady: async () => {
            try {
              const [pdfRes, infoRes] = await Promise.all([
                fetch(`${API_URL}/api/dossiers/${dossierRef}/pdf`, {
                  headers: { Authorization: `Bearer ${token}` }
                }),
                fetch(`${API_URL}/api/dossiers/${dossierRef}`, {
                  headers: { Authorization: `Bearer ${token}` }
                })
              ]);
              const blob = await pdfRes.blob();
              const info = infoRes.ok ? await infoRes.json() : {};
              const candidateName = candidate?.full_name?.replace(/\s+/g, '_') || 'Candidate';
              const position = requirement?.position_title?.replace(/\s+/g, '_') || 'Position';
              const filename = `RedactedCV_${candidateName}_${position}.pdf`;
              const pdfUrl = window.URL.createObjectURL(blob);

              setGeneratedRedactedCvResult({
                candidateId: candidate.id,
                candidateName: candidate?.full_name || 'Candidate',
                dossierRef,
                shareToken: info.share_token,
                pdfUrl,
                filename,
                options
              });

              setRedactedCvModalOpen(false);
              setRedactedCvResultModalOpen(true);
              loadCandidateDossiers(candidate.id);
              toast.success('Redacted CV generated successfully!');
            } catch (e) {
              console.error('Error loading finished redacted CV:', e);
              toast.error('Redacted CV was generated, but the PDF could not be loaded. Check Generated Documents.');
              loadCandidateDossiers(candidate.id);
            }
            setGeneratingRedactedCv(false);
          },
          onFailed: (errorMessage) => {
            toast.error(errorMessage || 'Failed to generate redacted CV');
            setGeneratingRedactedCv(false);
          },
          onTimeout: () => {
            toast.info('Still working on the redacted CV — check back in a bit; it will appear in Generated Documents.');
            loadCandidateDossiers(candidate.id);
            setGeneratingRedactedCv(false);
          }
        });
      } else {
        // For error responses, read as text and parse
        const errorText = await res.text();
        let errorMessage = 'Failed to generate redacted CV';
        try {
          const err = JSON.parse(errorText);
          errorMessage = err.detail || errorMessage;
        } catch {
          if (errorText) errorMessage = errorText;
        }
        toast.error(errorMessage);
        setGeneratingRedactedCv(false);
      }
    } catch (e) {
      console.error('Error generating redacted CV:', e);
      toast.error(e.message || 'Error generating redacted CV');
      setGeneratingRedactedCv(false);
    }
  };

  // Download redacted CV PDF
  const downloadRedactedCvPdf = () => {
    if (generatedRedactedCvResult?.pdfUrl) {
      const link = document.createElement('a');
      link.href = generatedRedactedCvResult.pdfUrl;
      link.download = generatedRedactedCvResult.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('PDF downloaded!');
    }
  };

  // Regenerate redacted CV (async - see pollRedactedCvStatus above)
  const handleRegenerateRedactedCv = async (dossierRef, candidateId, existingOptions = {}) => {
    setRegeneratingDossier(dossierRef);
    const token = localStorage.getItem('ats_token');
    try {
      const formData = new FormData();
      // Use existing options if available
      if (existingOptions.hide_name !== undefined) {
        formData.append('hide_name', existingOptions.hide_name);
      }
      if (existingOptions.hide_employers !== undefined) {
        formData.append('hide_employers', existingOptions.hide_employers);
      }

      const res = await fetch(`${API_URL}/api/dossiers/${dossierRef}/regenerate-redacted-cv`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        toast.info('Regenerating… this can take up to a couple of minutes.');

        pollRedactedCvStatus(dossierRef, {
          onReady: async () => {
            try {
              const pdfRes = await fetch(`${API_URL}/api/dossiers/${dossierRef}/pdf`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              const blob = await pdfRes.blob();
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `RedactedCV_${dossierRef}_regenerated.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);

              loadCandidateDossiers(candidateId);
              toast.success('Redacted CV regenerated and downloaded!');
            } catch (e) {
              console.error('Error downloading regenerated redacted CV:', e);
              toast.error('Redacted CV was regenerated, but the PDF could not be downloaded. Check Generated Documents.');
              loadCandidateDossiers(candidateId);
            }
            setRegeneratingDossier(null);
          },
          onFailed: (errorMessage) => {
            toast.error(errorMessage || 'Failed to regenerate redacted CV');
            setRegeneratingDossier(null);
          },
          onTimeout: () => {
            toast.info('Still regenerating the redacted CV — check back in a bit.');
            loadCandidateDossiers(candidateId);
            setRegeneratingDossier(null);
          }
        });
      } else {
        // For error responses, read as text and parse
        const errorText = await res.text();
        let errorMessage = 'Failed to regenerate redacted CV';
        try {
          const err = JSON.parse(errorText);
          errorMessage = err.detail || errorMessage;
        } catch {
          if (errorText) errorMessage = errorText;
        }
        toast.error(errorMessage);
        setRegeneratingDossier(null);
      }
    } catch (e) {
      toast.error(e.message || 'Error regenerating redacted CV');
      setRegeneratingDossier(null);
    }
  };

  const openInterviewModal = (candidate) => {
    setSelectedCandidateForInterview(candidate);
    setInterviewForm({
      round_name: 'Interview',
      mode: 'phone',
      scheduled_at: '',
      location: '',
      meeting_link: '',
      client_contact_name: '',
      client_contact_phone: '',
      notes: ''
    });
    setInterviewModalOpen(true);
  };

  const handleScheduleInterview = async (e) => {
    e.preventDefault();
    if (!interviewForm.scheduled_at) {
      toast.error('Please select date and time');
      return;
    }
    if (interviewForm.mode === 'video' && !interviewForm.meeting_link) {
      toast.error('Meeting link is required for video interviews');
      return;
    }
    if (interviewForm.mode === 'in_person' && !interviewForm.location) {
      toast.error('Location is required for in-person interviews');
      return;
    }

    setSchedulingInterview(true);
    try {
      const token = localStorage.getItem('ats_token');
      const scheduledAt = new Date(interviewForm.scheduled_at).toISOString();
      
      const payload = {
        candidate_id: selectedCandidateForInterview.id,
        requirement_id: id,
        round_name: interviewForm.round_name,
        mode: interviewForm.mode,
        scheduled_at: scheduledAt,
        location: interviewForm.location || null,
        meeting_link: interviewForm.meeting_link || null,
        client_contact_name: interviewForm.client_contact_name || null,
        client_contact_phone: interviewForm.client_contact_phone || null,
        notes: interviewForm.notes || null
      };

      const res = await fetch(`${API_URL}/api/interviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success('Interview scheduled! Confirmation email sent.');
        setInterviewModalOpen(false);
        loadCandidates();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to schedule interview');
      }
    } catch (e) {
      console.error('Error:', e);
      toast.error('Error scheduling interview');
    }
    setSchedulingInterview(false);
  };

  const filteredCandidates = activeTab === 'all' 
    ? candidates 
    : candidates.filter(c => c.status === activeTab);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="max-w-7xl mx-auto py-12 px-4 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Back Button & Title */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/ats/requirements')}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-800" data-testid="requirement-title">
              {requirement?.position_title || 'Requirement'}
            </h1>
            <p className="text-sm text-slate-500">
              Manage candidates for this requirement
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={() => setAddCandidatesOpen(true)}
              data-testid="add-candidates-from-db-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Candidates
            </Button>
            <AddToRequirementDialog
              open={addCandidatesOpen}
              onOpenChange={setAddCandidatesOpen}
              mode="requirement"
              requirementId={id}
              requirementTitle={requirement?.position_title || ''}
              onAdded={() => { loadCandidates(); }}
            />
            <Button variant="outline" onClick={() => setManualModalOpen(true)} data-testid="add-manual-candidate-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Manual
            </Button>
            <Button onClick={() => { resetUploadModal(); setUploadModalOpen(true); }} data-testid="upload-resume-btn">
              <Upload className="w-4 h-4 mr-2" />
              Upload Resume
            </Button>
          </div>
        </div>

        {/* Requirement Skills Summary */}
        {requirement && (requirement.jd_skills_must?.length > 0 || requirement.jd_skills_nice?.length > 0) && (
          <Card className="mb-6">
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4">
                {requirement.jd_skills_must?.length > 0 && (
                  <div>
                    <span className="text-xs text-slate-500 font-medium">Must-Have Skills:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {requirement.jd_skills_must.slice(0, 8).map((skill, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">{skill}</Badge>
                      ))}
                      {requirement.jd_skills_must.length > 8 && (
                        <Badge variant="outline" className="text-xs">+{requirement.jd_skills_must.length - 8} more</Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stage Tabs */}
        <WhoFitsPanel requirementId={requirement?.id || id} />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="all" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
              All ({candidates.length})
            </TabsTrigger>
            {STAGES.filter(s => s !== 'rejected').map(stage => (
              <TabsTrigger 
                key={stage} 
                value={stage}
                className="data-[state=active]:bg-slate-800 data-[state=active]:text-white"
              >
                {STAGE_CONFIG[stage].label} ({stageCounts[stage] || 0})
              </TabsTrigger>
            ))}
            <TabsTrigger value="rejected" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
              Rejected ({stageCounts.rejected || 0})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Admin Template Override (Build 3) - Only visible to admins */}
        {isAdmin && availableTemplates.length > 0 && (
          <Card className="mb-6 border-amber-200 bg-amber-50/50">
            <CardContent className="py-3">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-amber-700">
                  <Briefcase className="w-4 h-4" />
                  <span className="text-sm font-medium">Admin Template Override</span>
                </div>
                <Select value={adminTemplateOverride} onValueChange={setAdminTemplateOverride}>
                  <SelectTrigger className="w-[200px] bg-white" data-testid="admin-template-override">
                    <SelectValue placeholder="Auto (AI Selection)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (AI Selection)</SelectItem>
                    {availableTemplates.map(t => (
                      <SelectItem key={t.template_id} value={t.template_id}>
                        {t.name || t.template_id} (v{t.version})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-amber-600">
                  Overrides AI template selection for new dossiers
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Candidates Table */}
          <div className="lg:col-span-2">
            {filteredCandidates.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="font-medium">No candidates {activeTab !== 'all' ? `in ${STAGE_CONFIG[activeTab]?.label} stage` : ''}</p>
                  <p className="text-sm mt-1">Upload a resume or add candidates manually</p>
                  <Button onClick={() => setUploadModalOpen(true)} className="mt-4">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Resume
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Selection Actions Bar */}
                {candidates.length > 0 && (
                  <div className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2 border">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedCandidateIds.length === candidates.length && candidates.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-slate-300"
                        data-testid="select-all-candidates"
                      />
                      <span className="text-sm text-slate-600">
                        {selectedCandidateIds.length > 0 
                          ? `${selectedCandidateIds.length} selected` 
                          : 'Select candidates'}
                      </span>
                    </div>
                    {selectedCandidateIds.length > 0 && (
                      <Button
                        onClick={openSubmissionComposer}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid="submit-to-client-btn"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Submit to Client ({selectedCandidateIds.length})
                      </Button>
                    )}
                  </div>
                )}
                
                {filteredCandidates.map(cand => (
                  <Card key={cand.id} data-testid={`candidate-card-${cand.id}`} className={selectedCandidateIds.includes(cand.id) ? 'ring-2 ring-emerald-500' : ''}>
                    <CardContent className="p-4">
                      {/* Main candidate row */}
                      <div className="flex items-start justify-between gap-4">
                        {/* Checkbox for selection */}
                        <div className="pt-1">
                          <input
                            type="checkbox"
                            checked={selectedCandidateIds.includes(cand.id)}
                            onChange={() => toggleCandidateSelection(cand.id)}
                            className="w-4 h-4 rounded border-slate-300"
                            data-testid={`select-candidate-${cand.id}`}
                          />
                        </div>
                        
                        {/* Candidate Info */}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-800">{cand.full_name}</div>
                          {cand.current_designation && (
                            <div className="text-sm text-slate-500">{cand.current_designation}</div>
                          )}
                          {cand.current_company && (
                            <div className="text-sm text-slate-400">{cand.current_company}</div>
                          )}
                          {cand.email && (
                            <div className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                              <Mail className="w-3 h-3" />{cand.email}
                            </div>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                            {cand.total_experience_years && (
                              <span>{cand.total_experience_years} yrs exp</span>
                            )}
                            {cand.current_ctc && (
                              <span>₹{cand.current_ctc} LPA</span>
                            )}
                            {cand.notice_period_days && (
                              <span>{cand.notice_period_days}d notice</span>
                            )}
                          </div>
                        </div>

                        {/* Stage & Actions */}
                        <div className="flex flex-col items-end gap-2">
                          <Select 
                            value={cand.status} 
                            onValueChange={(v) => handleStageChange(cand.id, v)}
                          >
                            <SelectTrigger className="w-[130px] h-8" data-testid={`stage-select-${cand.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STAGES.map(stage => (
                                <SelectItem key={stage} value={stage}>
                                  <span className={`px-2 py-0.5 rounded text-xs ${STAGE_CONFIG[stage].color}`}>
                                    {STAGE_CONFIG[stage].label}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          <div className="flex items-center gap-1">
                            {cand.resume_storage_path && (
                              <a
                                href={`${API_URL}/api/candidate/${cand.source_candidate_id || cand.id}/resume`}
                                target="_blank"
                                rel="noreferrer"
                                className="p-1.5 text-slate-400 hover:text-green-600 rounded"
                                title="Download Resume"
                              >
                                <Download className="w-4 h-4" />
                              </a>
                            )}
                            <button
                              onClick={() => handleGeneratePdfDossier(cand.id)}
                              disabled={generatingPdfDossier === cand.id}
                              className="p-1.5 text-slate-400 hover:text-emerald-600 rounded disabled:opacity-50"
                              title="Generate PDF Dossier"
                              data-testid={`generate-pdf-dossier-${cand.id}`}
                            >
                              {generatingPdfDossier === cand.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <FileText className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => openRedactedCvModal(cand)}
                              className="p-1.5 text-slate-400 hover:text-amber-600 rounded"
                              title="Generate Redacted CV"
                              data-testid={`generate-redacted-cv-${cand.id}`}
                            >
                              <ShieldCheck className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openInterviewModal(cand)}
                              className="p-1.5 text-slate-400 hover:text-purple-600 rounded"
                              title="Schedule Interview"
                              data-testid={`schedule-interview-${cand.id}`}
                            >
                              <Calendar className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => confirmDelete(cand)}
                              className="p-1.5 text-slate-400 hover:text-red-600 rounded"
                              title="Remove"
                              data-testid={`delete-candidate-${cand.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Collapsible Dossier History Section */}
                      <Collapsible 
                        open={dossierSectionsOpen[cand.id]} 
                        onOpenChange={() => toggleDossierSection(cand.id)}
                      >
                        <CollapsibleTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="w-full mt-3 text-slate-500 hover:text-slate-700 justify-between"
                            data-testid={`dossier-history-toggle-${cand.id}`}
                          >
                            <span className="flex items-center gap-2">
                              <History className="w-4 h-4" />
                              Generated Dossiers
                              {candidateDossiers[cand.id]?.length > 0 && (
                                <Badge variant="secondary" className="text-xs">
                                  {candidateDossiers[cand.id].length}
                                </Badge>
                              )}
                            </span>
                            {dossierSectionsOpen[cand.id] ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="mt-3 pt-3 border-t space-y-2">
                            {loadingDossiers[cand.id] ? (
                              <div className="flex justify-center py-4">
                                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                              </div>
                            ) : candidateDossiers[cand.id]?.length > 0 ? (
                              candidateDossiers[cand.id].map(dossier => (
                                <div 
                                  key={dossier.dossier_ref} 
                                  className={`p-3 rounded-lg ${dossier.kind === 'redacted_cv' ? 'bg-amber-50' : 'bg-slate-50'}`}
                                  data-testid={`dossier-item-${dossier.dossier_ref}`}
                                >
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <span className="font-mono text-sm font-medium text-slate-700">
                                          {dossier.dossier_ref}
                                        </span>
                                        {dossier.kind === 'redacted_cv' ? (
                                          <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300">
                                            <ShieldCheck className="w-3 h-3 mr-1" />
                                            Redacted CV
                                          </Badge>
                                        ) : (
                                          <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300">
                                            <FileText className="w-3 h-3 mr-1" />
                                            Dossier
                                          </Badge>
                                        )}
                                      </div>
                                      <div className="text-xs text-slate-500 mt-0.5">
                                        {dossier.kind === 'redacted_cv' ? (
                                          <>
                                            {dossier.options?.hide_name && <span className="mr-2">🔒 Name hidden</span>}
                                            {dossier.options?.hide_employers && <span>🏢 Employers hidden</span>}
                                            {!dossier.options?.hide_name && !dossier.options?.hide_employers && 'Full details shown'}
                                          </>
                                        ) : (
                                          <>Template: {dossier.template_id} v{dossier.template_version}</>
                                        )}
                                      </div>
                                      <div className="text-xs text-slate-400 mt-0.5">
                                        {new Date(dossier.generated_at).toLocaleDateString('en-IN', {
                                          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                        })}
                                        {dossier.regeneration_count > 0 && (
                                          <span className="ml-2 text-amber-600">
                                            (Regenerated {dossier.regeneration_count}x)
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={() => viewDossierPreview(dossier.share_token)}
                                        className="p-1.5 text-slate-400 hover:text-blue-600 rounded"
                                        title="View Preview"
                                      >
                                        <Eye className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => downloadSavedDossierPdf(dossier.dossier_ref)}
                                        className="p-1.5 text-slate-400 hover:text-green-600 rounded"
                                        title="Download PDF"
                                      >
                                        <Download className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => copyShareLink(dossier.share_token)}
                                        className="p-1.5 text-slate-400 hover:text-purple-600 rounded"
                                        title="Copy Share Link"
                                      >
                                        <Link2 className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => dossier.kind === 'redacted_cv' 
                                          ? handleRegenerateRedactedCv(dossier.dossier_ref, cand.id, dossier.options)
                                          : handleRegenerateDossier(dossier.dossier_ref, cand.id)
                                        }
                                        disabled={regeneratingDossier === dossier.dossier_ref}
                                        className="p-1.5 text-slate-400 hover:text-amber-600 rounded disabled:opacity-50"
                                        title={dossier.kind === 'redacted_cv' ? 'Regenerate Redacted CV' : 'Regenerate Dossier'}
                                        data-testid={`regenerate-dossier-${dossier.dossier_ref}`}
                                      >
                                        {regeneratingDossier === dossier.dossier_ref ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <RefreshCw className="w-4 h-4" />
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="text-center py-4 text-slate-400 text-sm">
                                No documents generated yet
                              </div>
                            )}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Matching Candidates Sidebar */}
          <div>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Target className="w-5 h-5 text-green-600" />
                  Matching Candidates
                  <Badge variant="secondary" className="ml-auto">{matchingCandidates.length}</Badge>
                </CardTitle>
                <CardDescription>
                  90%+ skill match from your database
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingMatches ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : matchingCandidates.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 text-sm">
                    No matching candidates found in database
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {matchingCandidates.map(match => (
                      <div 
                        key={match.id} 
                        className="p-3 border rounded-lg hover:bg-slate-50 transition-colors"
                        data-testid={`match-candidate-${match.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="font-medium text-sm">{match.full_name}</div>
                            {match.current_designation && (
                              <div className="text-xs text-slate-500">{match.current_designation}</div>
                            )}
                            {match.current_company && (
                              <div className="text-xs text-slate-400">{match.current_company}</div>
                            )}
                          </div>
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            {match.match_score}% match
                          </Badge>
                        </div>
                        {match.skills?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {match.skills.slice(0, 5).map((skill, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs py-0">{skill}</Badge>
                            ))}
                          </div>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="w-full mt-2"
                          onClick={() => handleAddFromDatabase(match.id)}
                          disabled={addingFromDb === match.id}
                          data-testid={`add-match-${match.id}`}
                        >
                          {addingFromDb === match.id ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <UserPlus className="w-3 h-3 mr-1" />
                          )}
                          Add to Requirement
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Upload Resume Modal */}
        <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Upload Candidate Resume
              </DialogTitle>
              <DialogDescription>
                Upload a resume to automatically extract candidate details
              </DialogDescription>
            </DialogHeader>

            {!parsedData ? (
              <div className="space-y-4">
                <div>
                  <Label>Resume File (PDF, DOCX, TXT) *</Label>
                  <Input
                    type="file"
                    accept=".pdf,.docx,.doc,.txt"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    className="mt-1"
                    data-testid="resume-file-input"
                  />
                </div>

                <div>
                  <Label>AI Provider</Label>
                  <Select value={aiProvider} onValueChange={setAiProvider}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="anthropic">Claude (Anthropic)</SelectItem>
                      <SelectItem value="openai">GPT-4 (OpenAI)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setUploadModalOpen(false)}>Cancel</Button>
                  <Button onClick={handleResumeUpload} disabled={uploading || !selectedFile} data-testid="parse-resume-btn">
                    {uploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Parse Resume
                  </Button>
                </DialogFooter>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-800">Resume Parsed Successfully</p>
                    <p className="text-sm text-green-700">Review and edit the extracted details</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Full Name *</Label>
                    <Input
                      value={parsedData.full_name || ''}
                      onChange={(e) => setParsedData({...parsedData, full_name: e.target.value})}
                      data-testid="parsed-name-input"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={parsedData.email || ''}
                      onChange={(e) => setParsedData({...parsedData, email: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={parsedData.phone || ''}
                      onChange={(e) => setParsedData({...parsedData, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Current Company</Label>
                    <Input
                      value={parsedData.current_company || ''}
                      onChange={(e) => setParsedData({...parsedData, current_company: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Designation</Label>
                    <Input
                      value={parsedData.current_designation || ''}
                      onChange={(e) => setParsedData({...parsedData, current_designation: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Experience (Years)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={parsedData.total_experience_years || ''}
                      onChange={(e) => setParsedData({...parsedData, total_experience_years: parseFloat(e.target.value) || null})}
                    />
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input
                      value={parsedData.location || ''}
                      onChange={(e) => setParsedData({...parsedData, location: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Current CTC (LPA)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={parsedData.current_ctc || ''}
                      onChange={(e) => setParsedData({...parsedData, current_ctc: parseFloat(e.target.value) || null})}
                    />
                  </div>
                  <div>
                    <Label>Expected CTC (LPA)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={parsedData.expected_ctc || ''}
                      onChange={(e) => setParsedData({...parsedData, expected_ctc: parseFloat(e.target.value) || null})}
                    />
                  </div>
                  <div>
                    <Label>Notice Period (Days)</Label>
                    <Input
                      type="number"
                      value={parsedData.notice_period_days || ''}
                      onChange={(e) => setParsedData({...parsedData, notice_period_days: parseInt(e.target.value) || null})}
                    />
                  </div>
                </div>

                <div>
                  <Label>Skills</Label>
                  <div className="flex flex-wrap gap-1 mt-1 p-2 border rounded-md min-h-[40px]">
                    {(parsedData.skills || []).map((skill, idx) => (
                      <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                        {skill}
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...parsedData.skills];
                            updated.splice(idx, 1);
                            setParsedData({...parsedData, skills: updated});
                          }}
                          className="ml-1 hover:text-red-500"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Summary</Label>
                  <Textarea
                    value={parsedData.summary || ''}
                    onChange={(e) => setParsedData({...parsedData, summary: e.target.value})}
                    rows={2}
                  />
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setParsedData(null)}>Back</Button>
                  <Button onClick={handleSaveFromResume} disabled={saving} data-testid="save-parsed-candidate-btn">
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Add Candidate
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Manual Add Modal */}
        <Dialog open={manualModalOpen} onOpenChange={setManualModalOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Candidate Manually</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleManualSubmit} className="space-y-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={manualForm.full_name}
                  onChange={(e) => setManualForm({...manualForm, full_name: e.target.value})}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={manualForm.email}
                    onChange={(e) => setManualForm({...manualForm, email: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={manualForm.phone}
                    onChange={(e) => setManualForm({...manualForm, phone: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Current Company</Label>
                  <Input
                    value={manualForm.current_company}
                    onChange={(e) => setManualForm({...manualForm, current_company: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Designation</Label>
                  <Input
                    value={manualForm.current_designation}
                    onChange={(e) => setManualForm({...manualForm, current_designation: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Experience (Yrs)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={manualForm.total_experience_years}
                    onChange={(e) => setManualForm({...manualForm, total_experience_years: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Current CTC</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={manualForm.current_ctc}
                    onChange={(e) => setManualForm({...manualForm, current_ctc: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Expected CTC</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={manualForm.expected_ctc}
                    onChange={(e) => setManualForm({...manualForm, expected_ctc: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Notice Period (Days)</Label>
                  <Input
                    type="number"
                    value={manualForm.notice_period_days}
                    onChange={(e) => setManualForm({...manualForm, notice_period_days: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Location</Label>
                  <Input
                    value={manualForm.location}
                    onChange={(e) => setManualForm({...manualForm, location: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label>Skills (comma-separated)</Label>
                <Input
                  value={manualForm.skills}
                  onChange={(e) => setManualForm({...manualForm, skills: e.target.value})}
                  placeholder="React, Node.js, Python"
                />
              </div>

              <div>
                <Label>Summary</Label>
                <Textarea
                  value={manualForm.summary}
                  onChange={(e) => setManualForm({...manualForm, summary: e.target.value})}
                  rows={2}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setManualModalOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Add Candidate
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600">Remove Candidate</DialogTitle>
              <DialogDescription>
                Are you sure you want to remove <strong>{candidateToDelete?.full_name}</strong> from this requirement?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
                {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Remove
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dossier Link Modal */}
        <Dialog open={dossierModalOpen} onOpenChange={setDossierModalOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-green-600" />
                Dossier Generated
              </DialogTitle>
              <DialogDescription>
                Share this link with your client to review the candidate profile
              </DialogDescription>
            </DialogHeader>

            {dossierLink && (
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <label className="text-xs text-slate-500 font-medium">Shareable Link</label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 text-sm bg-white border rounded px-3 py-2 truncate">
                      {window.location.origin}/cv/{dossierLink.token}
                    </code>
                    <Button size="sm" variant="outline" onClick={copyDossierLink}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm text-slate-600">
                  <span>Expires in 30 days</span>
                  <a
                    href={`/cv/${dossierLink.token}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    Preview <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-800">
                  <p className="font-medium mb-1">What the client will see:</p>
                  <ul className="list-disc list-inside text-blue-700 space-y-0.5">
                    <li>Candidate profile with skills & experience</li>
                    <li>AI-generated cover letter</li>
                    <li>Resume download</li>
                    <li>Approve / Reject buttons</li>
                  </ul>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setDossierModalOpen(false)}>Close</Button>
              <Button onClick={copyDossierLink}>
                <Copy className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* PDF Dossier Result Modal (Build 3) - Shows after generation with Download/Share/Regenerate */}
        <Dialog open={dossierResultModalOpen} onOpenChange={setDossierResultModalOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="dossier-result-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Dossier Generated Successfully
              </DialogTitle>
              <DialogDescription>
                {generatedDossierResult?.candidateName}&apos;s professional dossier is ready
              </DialogDescription>
            </DialogHeader>

            {generatedDossierResult && (
              <div className="space-y-4">
                {/* Dossier Details */}
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <label className="text-xs text-emerald-600 font-medium">Reference Number</label>
                      <div className="font-mono font-medium text-emerald-800">
                        {generatedDossierResult.dossierRef}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-emerald-600 font-medium">Template Used</label>
                      <div className="font-medium text-emerald-800 capitalize">
                        {generatedDossierResult.templateUsed}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Share Link */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <label className="text-xs text-slate-500 font-medium">Share Link (View-only, expires in 72 hours)</label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 text-xs bg-white border rounded px-3 py-2 overflow-hidden text-ellipsis whitespace-nowrap block max-w-[350px]" title={`${window.location.origin}/cv/${generatedDossierResult.shareToken}`}>
                      {window.location.origin}/cv/{generatedDossierResult.shareToken}
                    </code>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => copyShareLink(generatedDossierResult.shareToken)}
                      data-testid="copy-share-link-btn"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => viewDossierPreview(generatedDossierResult.shareToken)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2">
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={downloadDossierPdf}
                    data-testid="download-dossier-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => copyShareLink(generatedDossierResult.shareToken)}
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Copy Share Link
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => {
                        handleRegenerateDossier(generatedDossierResult.dossierRef, generatedDossierResult.candidateId);
                        setDossierResultModalOpen(false);
                      }}
                      data-testid="regenerate-from-modal-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Regenerate
                    </Button>
                  </div>
                </div>

                <p className="text-xs text-slate-500 text-center">
                  Share link allows clients to view the dossier and download PDF without login
                </p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setDossierResultModalOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Redacted CV Options Modal */}
        <Dialog open={redactedCvModalOpen} onOpenChange={setRedactedCvModalOpen}>
          <DialogContent className="sm:max-w-md" data-testid="redacted-cv-options-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-amber-600" />
                Generate Redacted CV
              </DialogTitle>
              <DialogDescription>
                Create a client-safe CV for{' '}
                <strong>{selectedCandidateForRedactedCv?.full_name}</strong>
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <p className="font-medium mb-1">Redaction Applied:</p>
                <p className="text-xs">Phone, email, addresses, social URLs, DOB, and ID numbers are automatically removed.</p>
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium">Additional Privacy Options:</Label>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="hideName"
                    checked={redactedCvOptions.hideName}
                    onChange={(e) => setRedactedCvOptions(prev => ({ ...prev, hideName: e.target.checked }))}
                    className="w-4 h-4 rounded border-slate-300"
                    data-testid="hide-name-checkbox"
                  />
                  <label htmlFor="hideName" className="text-sm">
                    Hide candidate full name <span className="text-slate-500">(show initials only)</span>
                  </label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="hideEmployers"
                    checked={redactedCvOptions.hideEmployers}
                    onChange={(e) => setRedactedCvOptions(prev => ({ ...prev, hideEmployers: e.target.checked }))}
                    className="w-4 h-4 rounded border-slate-300"
                    data-testid="hide-employers-checkbox"
                  />
                  <label htmlFor="hideEmployers" className="text-sm">
                    Hide employer names <span className="text-slate-500">(replace with industry descriptors)</span>
                  </label>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setRedactedCvModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleGenerateRedactedCv}
                disabled={generatingRedactedCv}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="generate-redacted-cv-submit"
              >
                {generatingRedactedCv ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Working on it…
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Generate Redacted CV
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Redacted CV Result Modal */}
        <Dialog open={redactedCvResultModalOpen} onOpenChange={setRedactedCvResultModalOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="redacted-cv-result-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-amber-600" />
                Redacted CV Generated
              </DialogTitle>
              <DialogDescription>
                {generatedRedactedCvResult?.candidateName}&apos;s redacted CV is ready for client sharing
              </DialogDescription>
            </DialogHeader>

            {generatedRedactedCvResult && (
              <div className="space-y-4">
                {/* Details */}
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <label className="text-xs text-amber-600 font-medium">Reference Number</label>
                      <div className="font-mono font-medium text-amber-800">
                        {generatedRedactedCvResult.dossierRef}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-amber-600 font-medium">Options Applied</label>
                      <div className="text-amber-800 text-xs mt-0.5">
                        {generatedRedactedCvResult.options?.hideName && <div>✓ Name hidden</div>}
                        {generatedRedactedCvResult.options?.hideEmployers && <div>✓ Employers hidden</div>}
                        {!generatedRedactedCvResult.options?.hideName && !generatedRedactedCvResult.options?.hideEmployers && 
                          <div>Full details shown</div>
                        }
                      </div>
                    </div>
                  </div>
                </div>

                {/* Share Link */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <label className="text-xs text-slate-500 font-medium">Share Link (View-only, expires in 72 hours)</label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 text-xs bg-white border rounded px-3 py-2 overflow-hidden text-ellipsis whitespace-nowrap block max-w-[350px]" title={`${window.location.origin}/cv/${generatedRedactedCvResult.shareToken}`}>
                      {window.location.origin}/cv/{generatedRedactedCvResult.shareToken}
                    </code>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => copyShareLink(generatedRedactedCvResult.shareToken)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2">
                  <Button 
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    onClick={downloadRedactedCvPdf}
                    data-testid="download-redacted-cv-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => copyShareLink(generatedRedactedCvResult.shareToken)}
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Copy Share Link
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => {
                        handleRegenerateRedactedCv(
                          generatedRedactedCvResult.dossierRef, 
                          generatedRedactedCvResult.candidateId,
                          generatedRedactedCvResult.options
                        );
                        setRedactedCvResultModalOpen(false);
                      }}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Regenerate
                    </Button>
                  </div>
                </div>

                <p className="text-xs text-slate-500 text-center">
                  This CV has personal contact information redacted for client-safe sharing
                </p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setRedactedCvResultModalOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Schedule Interview - v2 dialog (.ics + Google Calendar + reminders) */}
        <ScheduleInterviewDialog
          open={interviewModalOpen}
          onOpenChange={setInterviewModalOpen}
          candidate={selectedCandidateForInterview}
          requirementId={id}
          clientId={requirement?.client_id}
          onSaved={() => { setInterviewModalOpen(false); loadCandidates(); }}
        />
      </main>
    </div>
  );
}
